import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@platform/core/lib/format';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { fileTone } from '../fileTone';
import { useAgo } from '../useAgo';
import { fileSizeLabel } from './fileLimits';
import type { IFileRowProps } from './IFileRowProps';
import { previewKindOf } from './previewKind';

/**
 * Рядок файлу (прототип v4): плитка розширення 40×44 тоном типу (PDF, документ, таблиця, зображення, інше) · ім'я 600 (клік —
 * завантаження з авторизацією; зображення й PDF — перегляд) · «178 КБ · хто · коли» · «Переглянути» (зображення, PDF) · «Завантажити» ·
 * «Перейменувати» (поле на місці імені) · «Видалити».
 */
export function FileRow({ file, canEdit, downloading, onPreview, onDownload, onRename, onDelete }: IFileRowProps) {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const ago = useAgo();
  const [name, setName] = useState<string | null>(null);
  const tile = fileTone(file.fileName, file.mimeType);
  const viewable = previewKindOf(file.fileName, file.mimeType) !== null;
  const openFile = () => (viewable ? onPreview(file) : onDownload(file));
  const meta = `${fileSizeLabel(file.sizeBytes, lang === 'uk' ? 'КБ' : 'KB', lang === 'uk' ? 'МБ' : 'MB')} · ${file.uploadedBy.name} · ${ago(file.uploadedAt)}`;
  const save = () => { if (name?.trim() && name.trim() !== file.fileName) void onRename(file, name.trim()).then((ok) => ok && setName(null)); else setName(null); };
  return (
    <div className="rtab-file">
      <span className="rtab-file__ext" style={{ background: tile.tone }}>{tile.ext}</span>
      <div className="rtab-file__main">
        {name === null ? (
          <button type="button" className="rtab-file__name" title={t(viewable ? 'recordPage.files.preview' : 'recordPage.files.download')} disabled={downloading} aria-busy={downloading} onClick={openFile}>{file.fileName}</button>
        ) : (
          <input className="rtab-file__input" autoFocus value={name} aria-label={t('recordPage.files.renameLabel')} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setName(null); }} />
        )}
        <span className="rtab-file__meta" title={formatDateTime(file.uploadedAt, lang)}>{meta}</span>
      </div>
      {name === null ? (
        <>
          {viewable && <button type="button" className="rtab-file__btn is-strong" onClick={() => onPreview(file)}>{t('recordPage.files.preview')}</button>}
          <button type="button" className="rtab-file__btn is-strong" onClick={() => onDownload(file)} disabled={downloading}>{t('recordPage.files.download')}</button>
          {canEdit && <button type="button" className="rtab-file__btn" title={t('recordPage.files.renameTitle')} onClick={() => setName(file.fileName)}>{t('recordPage.files.rename')}</button>}
          {canEdit && <button type="button" className="rtab-file__btn is-danger" title={t('recordPage.files.deleteTitle')} aria-label={t('recordPage.files.deleteTitle')} onClick={() => onDelete(file)}>{t('recordPage.files.delete')}</button>}
        </>
      ) : (
        <>
          <button type="button" className="rtab-file__btn is-primary" onClick={save}>{t('recordPage.files.save')}</button>
          <button type="button" className="rtab-file__btn" onClick={() => setName(null)}>{t('recordPage.files.cancel')}</button>
        </>
      )}
    </div>
  );
}
